import Primary from "../layouts/Primary";
import AllianceBar from "../components/AllianceBar";

import "./Bracket.css";
import {BracketHalf, SeriesAllianceState, TeamData,} from "../../javaTypes";
import {ReactNode, useContext, useEffect, useMemo, useRef, useState} from "react";
import {t} from "../../i18n";
import {RankingData} from "../rankingDataState";
import DisplayParams, {DisplayParamsType} from "../DisplayParams";
import {isBasicAllianceDescription, isDoubleElimsSeriesDescription, isSeriesAllianceDescription} from "../util";
import {Timeout} from "../displayContainer";
import MiniTimeout, {MiniTimeoutType} from "../layouts/MiniTimeout";
import {DisplayConfig} from "../Config";
import {useInterval} from "../../hooks";
import FormattedSeed from "../components/FormattedSeed";
import EventLogo from "../components/EventLogo";
import {CSSTransition, TransitionGroup} from "react-transition-group";

export default function Bracket({config, showDivisionLabel} : {config: DisplayConfig, showDivisionLabel?: boolean}) {
    const params = useContext(DisplayParams)
    const divisionNumber = params.divisionNumber || 0
    const state = useContext(RankingData);
    if(state[divisionNumber]?.playoffStructure?.type?.startsWith("ROUND_ROBIN")) {
        return <RoundRobinRankings />;
    } else {
        return <EliminationBracket config={config} showDivisionLabel={showDivisionLabel}/>;
    }
}

export function EliminationBracket({config, showDivisionLabel} : {config: DisplayConfig, showDivisionLabel?: boolean}) {
    const params = useContext(DisplayParams)
    const divisionNumber = params.divisionNumber || 0
    const bracketContainer = useRef<HTMLDivElement | null>(null);
    const state = useContext(RankingData);
    const timeout = useContext(Timeout);

    const bracket = state[divisionNumber]?.bracket;
    const structure = state[divisionNumber]?.playoffStructure;
    // Resort the tournament in a logical display order so that schedule-driven swaps don't create criss-crossing
    // lines on the bracket
    const sortedSeries = useMemo(() => {
        return structure ? [...structure.series].sort((a, b) => {
            if(a.round != b.round) {
                return a.round - b.round;
            }
            if(isDoubleElimsSeriesDescription(a) && isDoubleElimsSeriesDescription(b) && a.bracketHalf != b.bracketHalf) {
                return a.bracketHalf == 'LOWER' ? 1 : -1;
            }
            const aWinners: number[] = [isSeriesAllianceDescription(a.red) ? a.red.series : null, isSeriesAllianceDescription(a.blue) ? a.blue.series : null].filter(m => m != null) as number[]
            const bWinners: number[] = [isSeriesAllianceDescription(b.red) ? b.red.series : null, isSeriesAllianceDescription(b.blue) ? b.blue.series : null].filter(m => m != null) as number[]
            // If both series have any winners, sort by the series numbers of those winners
            const firstWinner = Math.min(...aWinners, ...bWinners, 999)
            if(firstWinner < 999) {
                return aWinners.includes(firstWinner) ? -1 : 1
            }
            return a.series - b.series
        }) : undefined
    }, [structure]);

    const maxLayers = Math.max(...(sortedSeries?.filter(p => bracket?.seriesData[p.series - 1]).map(p => p.round) || [0]))
    let maxBasicLayer = Math.max(...(sortedSeries?.filter(p => isBasicAllianceDescription(p.red) || isBasicAllianceDescription(p.blue)).map(p => p.round) || [0]))
    const maxInRoundHalf = Math.max(...(sortedSeries ? Object.values(sortedSeries.reduce((acc, p) => {
        acc[`${p.round}-${isDoubleElimsSeriesDescription(p) ? p.bracketHalf : 'CENTER'}`] ||= 0
        acc[`${p.round}-${isDoubleElimsSeriesDescription(p) ? p.bracketHalf : 'CENTER'}`]++
        return acc
    }, {} as Record<string, number>)) : [1]))
    const columnGap = maxLayers == 7 ? 30 : 300 / (maxLayers - 1);
    const [svgPaths, setSvgPaths] = useState<ReactNode>();

    // TODO this should be useLayoutEffect
    useEffect(() => {
        if(!bracketContainer.current || !sortedSeries) {
            setSvgPaths([])
            return;
        }

        const scale = parseFloat(getComputedStyle(bracketContainer.current).getPropertyValue("--display-scale"));
        const baseRect = bracketContainer.current.getBoundingClientRect();


        for(let i = 0; i < sortedSeries.length; i++) {
            const pairing = sortedSeries[i];
            const element = bracketContainer.current.getElementsByClassName(`series-${pairing.series}`)[0] as HTMLDivElement;

            if(!element) {
                continue;
            }
            element.style.marginTop = '0';
        }

        for(let i = 0; i < sortedSeries.length; i++) {
            const pairing = sortedSeries[i];
            const element = bracketContainer.current.getElementsByClassName(`series-${pairing.series}`)[0] as HTMLDivElement;
            if(!element) {
                continue;
            }
            const winnerMatch = sortedSeries.find(p =>
                isSeriesAllianceDescription(p.red) && p.red.state == SeriesAllianceState.WINNER && p.red.series == pairing.series ||
                isSeriesAllianceDescription(p.blue) && p.blue.state == SeriesAllianceState.WINNER && p.blue.series == pairing.series)
            if(!winnerMatch) {
                continue;
            }

            const bars = element.getElementsByClassName("allianceBar");
            const upperRect = bars[0].getBoundingClientRect();
            const lowerRect = bars[1].getBoundingClientRect();

            const winnerBarsContainer = bracketContainer.current.getElementsByClassName(`series-${winnerMatch.series}`)[0]
            if(!winnerBarsContainer) {
                continue;
            }

            const winnerBars = winnerBarsContainer.getElementsByClassName("allianceBar");
            const winnerBar = isSeriesAllianceDescription(winnerMatch.red) && winnerMatch.red.state == SeriesAllianceState.WINNER && winnerMatch.red.series == pairing.series ? winnerBars[0] : winnerBars[1]
            const winnerBarRect = winnerBar.getBoundingClientRect();

            const myStyles = getComputedStyle(element)
            const winnerStyles = getComputedStyle(winnerBarsContainer)
            if(winnerStyles.gridRowStart == myStyles.gridRowStart && winnerStyles.gridRowEnd == myStyles.gridRowEnd) {
                // Do an offset to make things line up pretty
                const centerWinnerBar = (winnerBarRect.y + winnerBarRect.height / 2) / scale
                const centerUpperBar = (upperRect.y + upperRect.height / 2) / scale
                const centerLowerBar = (lowerRect.y + lowerRect.height / 2) / scale
                const offset = centerWinnerBar - (centerUpperBar + centerLowerBar) / 2
                // Offset must be doubled because vertical centering means effectively only half the offset is applied directly
                element.style.marginTop = offset * 2 + "px"
            }
        }
        const paths: ReactNode[] = []
        for(let i = 0; i < sortedSeries.length; i++) {
            const pairing = sortedSeries[i];
            const element = bracketContainer.current.getElementsByClassName(`series-${pairing.series}`)[0];
            if(!element) {
                continue;
            }
            const winnerMatch = sortedSeries.find(p =>
                isSeriesAllianceDescription(p.red) && p.red.state == SeriesAllianceState.WINNER && p.red.series == pairing.series ||
                isSeriesAllianceDescription(p.blue) && p.blue.state == SeriesAllianceState.WINNER && p.blue.series == pairing.series)
            if(!winnerMatch) {
                continue;
            }

            const bars = element.getElementsByClassName("allianceBar");
            const upperRect = bars[0].getBoundingClientRect();
            const lowerRect = bars[1].getBoundingClientRect();

            const winnerBarsContainer = bracketContainer.current.getElementsByClassName(`series-${winnerMatch.series}`)[0]
            if(!winnerBarsContainer) {
                continue;
            }
            const upperBarStart = { x: (upperRect.x + upperRect.width - baseRect.x) / scale, y: (upperRect.y + upperRect.height / 2 - baseRect.y) / scale };
            const lowerBarStart = { x: (lowerRect.x + lowerRect.width - baseRect.x) / scale, y: (lowerRect.y + lowerRect.height / 2 - baseRect.y) / scale };
            paths.push(<path key={`bridge-${pairing.series}`} d={`M ${upperBarStart.x} ${upperBarStart.y} h ${columnGap/4} V ${lowerBarStart.y} h -${columnGap/4}`} stroke="white" fill="transparent" strokeWidth="4"/>)

            const winnerBars = winnerBarsContainer.getElementsByClassName("allianceBar");
            const winnerBar = isSeriesAllianceDescription(winnerMatch.red) && winnerMatch.red.state == SeriesAllianceState.WINNER && winnerMatch.red.series == pairing.series ? winnerBars[0] : winnerBars[1]
            const winnerBarRect = winnerBar.getBoundingClientRect();
            const winnerBarStart = { x: (winnerBarRect.x - baseRect.x) / scale, y: (winnerBarRect.y + winnerBarRect.height / 2 - baseRect.y) / scale };

            paths.push(<path key={`line-${pairing.series}`} d={`M ${upperBarStart.x + columnGap/4} ${(upperBarStart.y + lowerBarStart.y) / 2} h ${columnGap * (1 / 3)} V ${winnerBarStart.y} H ${winnerBarStart.x}`} stroke="white" fill="transparent" strokeWidth="4"/>)
        }
        setSvgPaths(paths)
    }, [sortedSeries, bracketContainer.current]);

    const firstUnplayed = Math.min(...(sortedSeries?.filter(p => {
        const layer = bracket?.seriesData[p.series - 1]
        const winnerExists = ((layer?.series?.red.wins || 0) >= p.matchesToWin || (layer?.series?.blue.wins || 0) >= p.matchesToWin)

        return !winnerExists
    }).map(p => p.series) || [0]))

    let allianceMap = new Map<number, TeamData[]>();
    const last = sortedSeries ? sortedSeries[sortedSeries.length - 1] : undefined;
    let hasExtraMatch = last && last.series && isDoubleElimsSeriesDescription(last) && last.isFinals && last.optional && !!bracket?.seriesData[last.series - 1];
    for (let b of bracket?.seriesData ?? []) {
        if (b?.series?.red && b?.series?.red.seed) {
            allianceMap.set(b.series?.red.alliance, b?.series?.red?.teams);
        }
        if (b?.series?.blue && b?.series?.blue.seed) {
            allianceMap.set(b.series?.blue.alliance, b?.series?.blue?.teams);
        }
    }
    const maxTeams = Math.max(...[...allianceMap.values()].map(x => x.filter(x => x).length));
    const hasByes = maxBasicLayer > 1
    const isDoubleElims = sortedSeries?.some(isDoubleElimsSeriesDescription)
    let cols = "repeat(${maxBasicLayer}, ${hasByes || maxInRoundHalf < 3 || !isDoubleElims ? 2 : 3}fr)"
    if(maxLayers - maxBasicLayer > 0) {
        cols = `${cols} repeat(${maxLayers - maxBasicLayer}, minmax(0, 1fr))`
    }
    const alliances = [...new Set(sortedSeries?.flatMap(p => {
        const ret = []
        if(isBasicAllianceDescription(p.red)) {
            ret.push(p.red.alliance)
        }
        if(isBasicAllianceDescription(p.blue)) {
            ret.push(p.blue.alliance)
        }
        return ret
    }).sort((a, b) => a - b))] || []
    const eliminatedAlliances: number[] = []
    for(const p of sortedSeries || []) {
        const layer = bracket?.seriesData[p.series - 1]
        const redWon = (layer?.series?.red.wins || 0) >= p.matchesToWin
        const blueWon = (layer?.series?.blue.wins || 0) >= p.matchesToWin
        const winnerExists = redWon || blueWon
        // A team is eliminated in lower bracket matches or in the last finals match to be played
        if(winnerExists && (!isDoubleElimsSeriesDescription(p) || p.bracketHalf == 'LOWER' || (p.isFinals && (p.optional || redWon)))) {
            if(redWon) {
                eliminatedAlliances.push(layer!.series.blue.alliance)
            } else if(blueWon) {
                eliminatedAlliances.push(layer!.series.red.alliance)
            }
        }
    }

    return <Primary qr={"/playoffs"} alternateCompact>
        {config.type !== "pit" ? <MiniTimeout type={MiniTimeoutType.TOP_RIGHT} config={config} timeout={timeout}/> : null}
        <div className={`bracket ${alliances.length > 6 ? 'shorter' : ''}`} style={{
            gridTemplateColumns: cols,
            gridTemplateRows: isDoubleElims ? `repeat(${maxInRoundHalf}, minmax(0, ${hasByes ? 3 : 2}fr)) repeat(${maxInRoundHalf}, minmax(0, ${hasByes ? 2 : 1}fr))` : `repeat(${maxInRoundHalf}, minmax(0, 1fr))`,
            columnGap: columnGap + "px"
        }} ref={bracketContainer}>
            {sortedSeries?.map((p, seriesIndex) => {
                const sectionElements = sortedSeries.filter(r => r.round == p.round && (!isDoubleElimsSeriesDescription(p) || !isDoubleElimsSeriesDescription(r) || p.bracketHalf == r.bracketHalf));
                const maxHeight = sectionElements.length == 1 && sectionElements[0].isFinals && isDoubleElims ? maxInRoundHalf * 2 : maxInRoundHalf
                const height = maxHeight / sectionElements.length;
                const slotInRound = sectionElements.indexOf(p)
                const halfOfRound = (isDoubleElimsSeriesDescription(p) ? p.bracketHalf == 'LOWER' : false) ? maxInRoundHalf + 1: 1
                const layer = bracket?.seriesData[p.series - 1]

                if(isDoubleElimsSeriesDescription(p) && p.optional && !layer) {
                    return null
                }

                const redWon = (layer?.series?.red.wins || 0) >= p.matchesToWin
                const blueWon = (layer?.series?.blue.wins || 0) >= p.matchesToWin
                const winnerExists = redWon || blueWon
                const hasTie = isDoubleElimsSeriesDescription(p) && p.matchesToWin === 1 && layer?.series?.ties;
                const col = isDoubleElimsSeriesDescription(p) ? layer?.series : 0;
                let matchSuffix = "";
                if (hasTie) {
                    matchSuffix = ` +${layer?.series?.ties}`;
                }
                const bracketIcons = {
                    win: <i className={"fa fa-trophy"}/>,
                    tie: <i className={"fa fa-minus"}/>,
                    drop: <i className={"fa fa-arrow-down"}/>,
                    elim: <i className={"fa fa-times"}/>
                };

                const seriesResultIcon = (won: boolean) => {
                    if (winnerExists) {
                        if (won) {
                            return bracketIcons.win;
                        }
                        if (!!bracket && isDoubleElimsSeriesDescription(p)) {
                            if (p.bracketHalf == BracketHalf.UPPER) {
                                return bracketIcons.drop
                            }
                            if (p.isFinals && p.series < bracket.seriesData.length && blueWon) { // finals with another finals after
                                return bracketIcons.drop
                            }
                        }
                        return bracketIcons.elim;
                    }
                    return hasTie ? bracketIcons.tie : null;
                }

                return <div key={p.series}
                  className={`allianceGroup series-${p.series} ${p.series == firstUnplayed ? 'current' : ''}`}
                  style={{
                      gridColumnStart: p.round,
                      gridColumnEnd: "span 1",
                      gridRowStart: halfOfRound + (slotInRound * height),
                      gridRowEnd: halfOfRound + ((slotInRound + 1) * height)
                  }}>
                    {/* If the series name starts with playoff, that is implied for the bracket */}
                    <span className="allianceGroupLabel">{p.name.replace(/^Playoff /, '')}<span style={{fontSize: '30px', position: 'relative', top: '-2px'}}>{matchSuffix}</span></span>
                    <AllianceBar vertical={maxInRoundHalf < 3 && maxLayers < 6} teamCount={1}
                                 color={"red"} alliance={[(!layer?.series?.red.seed ? '' : <FormattedSeed seed={layer.series.red.seed} compact={seriesIndex >= 4} />)]}
                                 seed={isSeriesAllianceDescription(p.red) ? <>{structure?.series[p.red.series - 1].shortName} {p.red.state == SeriesAllianceState.WINNER ? bracketIcons.win : bracketIcons.drop}</> : ''}
                                 winCount={(p.matchesToWin > 1 && (layer?.series?.red.wins || 0) < p.matchesToWin ? layer?.series?.red.wins : seriesResultIcon(redWon))}
                                 compressed />
                    <AllianceBar vertical={maxInRoundHalf < 3 && maxLayers < 6} teamsBeforeSeed={maxInRoundHalf < 3 && maxLayers < 6} teamCount={1}
                                 color={"blue"} alliance={[(!layer?.series?.blue.seed ? '' : <FormattedSeed seed={layer.series.blue.seed} compact={seriesIndex >= 4} />)]}
                                 seed={isSeriesAllianceDescription(p.blue) ? <>{structure?.series[p.blue.series - 1].shortName} {p.blue.state == SeriesAllianceState.WINNER ? bracketIcons.win : bracketIcons.drop}</> : ''}
                                 winCount={(p.matchesToWin > 1 && (layer?.series?.blue.wins || 0) < p.matchesToWin ? layer?.series?.blue.wins : seriesResultIcon(blueWon))}
                                 compressed />
                </div>
            })}
        </div>

        <div className={`bracketAllianceBars`} style={{
            gridTemplateRows: `repeat(${Math.ceil(alliances.length / 2)}, minmax(0, 1fr))`,
        }}>
            {alliances.map((i) => <AllianceBar withAvatar key={i} seed={i} teamCount={maxTeams} alliance={allianceMap.get(i)} compressed color={eliminatedAlliances.includes(i) ? 'grey' : undefined}/>)}
        </div>

        <svg className={"bracketLines"}>
            <defs>
                <filter id="shadow">
                    <feDropShadow dx="0.2" dy="0.4" stdDeviation="0.2" />
                </filter>
            </defs>
            {svgPaths}
        </svg>
    </Primary>;
}

export function RoundRobinRankings() {
    const params = useContext(DisplayParams)
    const divisionNumber = params.divisionNumber || 0
    const state = useContext(RankingData);

    const divState = state[divisionNumber].playoffRankings

    return <Primary>
        <table className={"roundRobinRankingsTable"}>
            <thead>
                <tr className={"tableHeading"}>
                    <th colSpan={7}>{t`${params.interdivisionName} Playoff Rankings`}</th>
                </tr>
                <tr>
                    <th style={{width: '150px'}}>{t`Rank`}</th>
                    <th style={{textAlign: "left", paddingLeft: '1.25em', width: '250px'}}>{t`Alliance`}</th>
                    <th>{t`Teams`}</th>
                    <th style={{width: '200px'}}>{t`CS`}</th>
                    <th style={{width: '200px'}}>{t`W-L-T`}</th>
                    <th style={{width: '200px'}}>{t`Avg Pts`}</th>
                    <th style={{width: '200px'}}>{t`Avg BASE`}</th>
                </tr>
            </thead>
            <tbody>
                {divState?.map((r, idx) => {
                    const division = params.divisions ? params.divisions[r.seed] : undefined
                    return <tr key={idx} className={r.rank == 'NP' ? 'unplayed' : 'played'}>
                        <td>{r.rank}</td>
                        <td style={{textAlign: "left"}}>
                            {division ? <span><EventLogo logo={division.logo} /> {division.name}</span> : r.seed}
                        </td>
                        <td>
                            <AllianceBar teamCount={r.teams.length} alliance={r.teams} compressed withAvatar />
                        </td>
                        <td>{r.rp}</td>
                        <td>{r.record}</td>
                        <td>{r.tbp1}</td>
                        <td>{r.tbp2}</td>
                    </tr>
                })}
            </tbody>
        </table>
    </Primary>;
}

export function RotatingBracket({config} : {config: DisplayConfig}) {
    const params = useContext(DisplayParams)
    const divisions = params.divisions || {}
    const divisionOrder = (config.divisionOrder || Object.keys(divisions).map(x => parseInt(x, 10)).sort((a, b) => a - b)).filter(x => divisions[x])
    const [selectedDivisionIdx, setSelectedDivisionIdx] = useState(0)
    useInterval(() => {
        setSelectedDivisionIdx(x => (x + 1) % (divisionOrder.length))
    }, 18000)
    const selectedDivision = divisionOrder[selectedDivisionIdx]
    const division = divisions[selectedDivision]
    if(params.style != 'default') {
        return
    }
    if(!division) {
        return <Primary>
            Cannot rotate divisional brackets without divisions
        </Primary>
    }

    const newParams: DisplayParamsType = {
        ...params,
        eventCode: params.eventCode.replace("_0", `_${selectedDivision}`),
        eventName: params.isFirstChampionship ? t`${division.name} Division` : t`${params.eventName} - ${division.name} Division`,
        divisionNumber: selectedDivision,
        isDualDivisionParent: false,
        divisions: {},
        eventLogo: division.logo,
        eventSponsor: division.sponsor,
        eventInverseSponsor: division.inverseSponsor,
    }

    return <TransitionGroup component={null}>
        <CSSTransition key={selectedDivision} timeout={1500} classNames="bracketTransition">
            <DisplayParams.Provider value={newParams}>
                <Bracket config={config} showDivisionLabel />
            </DisplayParams.Provider>
        </CSSTransition>
    </TransitionGroup>
}